import { RxInfoCircled } from "react-icons/rx";
import {
  AddAnotherInstanceButton,
  InstanceTitle,
  RemoveInstanceButton,
  ScrollableContent,
  SpaceInfoDontNeedApproval,
  StyledButton,
  StyledFormItem,
  StyledModal,
  StyledRadioGroup,
  TextDontNeedApproval,
} from "./style";

import { useState, useEffect, useMemo, useCallback } from "react";
import { Select, Radio, Form, Input } from "antd";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import api from "../../../../service";
import { useToast } from "../../../../hooks/toast";
import { useSelector } from "react-redux";
import { useLoading } from "../../../../hooks/loading";
import { useIntl } from "react-intl";
import { FaRegTrashAlt } from "react-icons/fa";
import { getUploadFlowEventsService } from "../../../../service/uploadFlow.service";
import CustomModalConfirmation from "../../../../components/CustomModalConfirmation";
import { IoCheckmarkDoneOutline, IoReturnUpBackOutline } from "react-icons/io5";

const DEFAULT_FORM_VALUES = Object.freeze({
  uploadFlow: null,
  needApproval: false,
  instances: [],
});

const DEFAULT_INSTANCE = Object.freeze({
  approverType: null,
  approverProfile: [],
  specifyUsers: "",
});

const EventUploadFlowModal = ({
  setIsOpen,
  modalIsOpen,
  eventEditionCode,
  updateCallback,
  associatedFlows,
  editingId,
  editingName,
  setEditingId,
}) => {
  const intl = useIntl();
  const [uploadFlows, setUploadFlows] = useState([]);
  const [allUploadFlows, setAllUploadFlows] = useState([]);
  const [approverProfiles, setApproverProfiles] = useState([]);
  const { addToast } = useToast();
  const { token } = useSelector((state) => state.auth);
  const { handleLoading } = useLoading();

  const [associationData, setAssociationData] = useState(null);

  const [modalConfirmIsOpen, setModalConfirmIsOpen] = useState(false);
  const [lastFormData, setLastFormData] = useState(null);

  const headerRequest = useMemo(
    () => ({
      "Content-Type": "application/json",
      Accept: "*/*",
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  useEffect(() => {
    if (!allUploadFlows?.length) return;

    const filtered = allUploadFlows.filter(
      (flow) => !associatedFlows.includes(flow.value)
    );

    const sorted = [...filtered].sort((a, b) => a.label.localeCompare(b.label));

    setUploadFlows(sorted);
  }, [associatedFlows, allUploadFlows]);

  useEffect(() => {
    if (!modalIsOpen) return;
    if (!editingId) return;

    let cancelled = false;

    const fetchAssociationData = async () => {
      try {
        const response = await getUploadFlowEventsService(
          editingId,
          eventEditionCode,
          headerRequest
        );

        if (cancelled) return;

        setAssociationData(response?.data?.data ?? null);
      } catch (err) {
        if (cancelled) return;

        addToast({
          type: "error",
          title: intl.formatMessage({ id: "error" }),
          description: intl.formatMessage({
            id: `api.error.${err?.response?.data?.description ?? "unknown"}`,
          }),
        });

        setAssociationData(null);
      }
    };

    fetchAssociationData();

    return () => {
      cancelled = true;
    };
  }, [modalIsOpen, editingId, eventEditionCode, headerRequest, addToast, intl]);

  const schema = useMemo(
    () =>
      z
        .object({
          uploadFlow: z.coerce
            .number({
              required_error: intl.formatMessage({ id: "select_upload_flow" }),
              invalid_type_error: intl.formatMessage({
                id: "select_upload_flow",
              }),
            })
            .min(1, intl.formatMessage({ id: "select_upload_flow" })),

          needApproval: z.boolean({
            required_error: intl.formatMessage({ id: "required_field" }),
            invalid_type_error: intl.formatMessage({ id: "required_field" }),
          }),

          instances: z
            .array(
              z
                .object({
                  approverType: z
                    .string({
                      required_error: intl.formatMessage({
                        id: "select_aproval_type",
                      }),
                      invalid_type_error: intl.formatMessage({
                        id: "select_aproval_type",
                      }),
                    })
                    .nonempty(
                      intl.formatMessage({ id: "select_aproval_type" })
                    ),

                  approverProfile: z.array(z.coerce.number()).default([]),

                  specifyUsers: z
                    .string()
                    .email(intl.formatMessage({ id: "EMAIL_INVALID" }))
                    .or(z.literal(""))
                    .default(""),
                })
                .refine(
                  (data) => {
                    if (data?.approverType === "profile") {
                      return (
                        Array.isArray(data.approverProfile) &&
                        data.approverProfile.length > 0
                      );
                    }
                    return true;
                  },
                  {
                    message: intl.formatMessage({
                      id: "select_at_least_one_approver_profile",
                    }),
                    path: ["approverType"],
                  }
                )
                .refine(
                  (data) => {
                    if (data?.approverType === "users") {
                      return !!data.specifyUsers;
                    }
                    return true;
                  },
                  {
                    message: intl.formatMessage({
                      id: "select_at_least_one_user",
                    }),
                    path: ["specifyUsers"],
                  }
                )
            )
            .default([]),
        })
        .refine(
          (data) => {
            if (data.needApproval) {
              return Array.isArray(data.instances) && data.instances.length > 0;
            }
            return true;
          },
          {
            message: "Instance is required",
            path: ["instances"],
          }
        ),
    [intl]
  );

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: DEFAULT_FORM_VALUES,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "instances",
  });

  const needApproval = watch("needApproval");
  const instancesWatch = watch("instances");

  const fetchData = useCallback(async () => {
    handleLoading(true);
    try {
      const [uploadFlowsResponse, approverProfilesResponse] = await Promise.all(
        [
          api.get("/upload-flow", {
            headers: headerRequest,
          }),
          api.patch(
            "/role",
            {
              name: "",
              active: true,
            },
            {
              headers: headerRequest,
            }
          ),
        ]
      );

      setAllUploadFlows(
        uploadFlowsResponse.data.flows
          .filter((flow) => flow.active)
          .map((flow) => ({
            label: flow.name,
            value: flow.id,
          }))
      );

      setApproverProfiles(
        approverProfilesResponse.data.roles.map((role) => ({
          label: role.name,
          value: role.id,
        }))
      );
    } catch (err) {
      addToast({
        type: "error",
        title: intl.formatMessage({ id: "error" }),
        description: intl.formatMessage({
          id: `api.error.${err?.response?.data?.description ?? "unknown"}`,
        }),
      });
    } finally {
      handleLoading(false);
    }
  }, [addToast, handleLoading, headerRequest, intl]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!editingId) return;
    if (!associationData?.flowId) return;

    const normalized = {
      uploadFlow: associationData.flowId,
      needApproval: !!associationData.needApproval,
      instances:
        associationData.instances?.map((i) => ({
          approverType: i?.approverType ?? null,
          approverProfile: i?.approverProfile ?? [],
          specifyUsers: i?.specifyUsers?.[0] ?? "",
        })) ?? [],
    };

    reset(normalized);
  }, [associationData, editingId, reset]);

  useEffect(() => {
    if (!modalIsOpen) return;

    const currentInstances = Array.isArray(instancesWatch)
      ? instancesWatch
      : [];

    if (needApproval === false) {
      if (currentInstances.length === 0) return;
      reset((prev) => ({ ...prev, instances: [] }));
      return;
    }

    if (needApproval === true) {
      if (currentInstances.length > 0) return;

      reset((prev) => ({
        ...prev,
        instances: [{ ...DEFAULT_INSTANCE }],
      }));
    }
  }, [modalIsOpen, needApproval, instancesWatch, reset]);

  const closeModal = useCallback(() => {
    setEditingId(null);
    setIsOpen(false);
    setAssociationData(null);
    reset(DEFAULT_FORM_VALUES);
  }, [reset, setEditingId, setIsOpen]);

  const submitToAPI = useCallback(
    async (data) => {
      const normalizedInstances =
        data.instances?.length > 0
          ? data.instances.map((instance) => ({
              ...instance,
              approverProfile:
                instance?.approverType === "profile"
                  ? instance.approverProfile
                  : [],
              specifyUsers:
                instance?.approverType === "users" && instance.specifyUsers
                  ? [instance.specifyUsers.trim()]
                  : [],
            }))
          : [];

      try {
        if (editingId) {
          const payload = {
            uploadFlowId: associationData?.flowId,
            eventEditionCode,
            needApproval: data.needApproval,
            instances: normalizedInstances,
          };

          await api.put("/upload-flow/associations", payload, {
            headers: headerRequest,
          });
        } else {
          const payload = {
            needApproval: data.needApproval,
            instances: normalizedInstances,
          };

          await api.post(
            `/upload-flow/${data.uploadFlow}/events/${eventEditionCode}`,
            payload,
            { headers: headerRequest }
          );
        }

        addToast({
          type: "success",
          title: intl.formatMessage({ id: "success" }),
          description: intl.formatMessage({
            id: !editingId
              ? "success.event_upload_flow_added"
              : "success.event_upload_flow_edited",
          }),
        });

        updateCallback();
        closeModal();
      } catch (error) {
        console.error("Erro ao adicionar/editar fluxo:", error);

        addToast({
          type: "error",
          title: intl.formatMessage({ id: "error" }),
          description: intl.formatMessage(
            { id: `api.error.${error?.response?.data?.code ?? "unknown"}` },
            { instanceWithError: error?.response?.data?.instanceWithError }
          ),
        });
      } finally {
        setEditingId(null);
      }
    },
    [
      addToast,
      intl,
      editingId,
      associationData,
      eventEditionCode,
      headerRequest,
      updateCallback,
      closeModal,
      setEditingId,
    ]
  );

  const handleFormSubmit = useCallback(
    async (data) => {
      setLastFormData(data);

      if (editingId && associationData?.confirmEdit === true) {
        setModalConfirmIsOpen(true);
        return;
      }

      await submitToAPI(data);
    },
    [editingId, associationData, submitToAPI]
  );

  const selectOptions = useMemo(() => {
    if (!editingId || !associationData?.flowId) return uploadFlows;

    const exists = uploadFlows.some(
      (f) => Number(f.value) === Number(associationData.flowId)
    );

    if (exists) return uploadFlows;

    return [
      {
        label: associationData.flowName,
        value: Number(associationData.flowId),
      },
      ...uploadFlows,
    ];
  }, [uploadFlows, editingId, associationData]);

  return (
    <StyledModal
      width={677}
      title={
        !editingId
          ? intl.formatMessage({ id: "add_event_file_upload_flow" })
          : intl.formatMessage({ id: "edit_event_file_upload_flow" })
      }
      open={modalIsOpen}
      onCancel={closeModal}
      footer={null}
      centered
      closable={false}
      destroyOnClose={true}
    >
      <Form layout="vertical" onFinish={handleSubmit(handleFormSubmit)}>
        <StyledFormItem
          label={intl.formatMessage({ id: "file_upload_flows" })}
          required
          validateStatus={errors.uploadFlow ? "error" : ""}
          help={errors.uploadFlow?.message}
        >
          <Controller
            name="uploadFlow"
            control={control}
            disabled={!!editingId}
            render={({ field }) => (
              <div data-testid="upload-flow-select">
                <Select
                  {...field}
                  popupClassName="upload-flow-select-dropdown"
                  options={selectOptions}
                  fieldNames={{ label: "label", value: "value" }}
                  placeholder={intl.formatMessage({ id: "file_upload_flows" })}
                />
              </div>
            )}
          />
        </StyledFormItem>

        <StyledFormItem
          label={intl.formatMessage({ id: "need_approval" })}
          required
          validateStatus={errors.needApproval ? "error" : ""}
          help={errors.needApproval?.message}
        >
          <Controller
            name="needApproval"
            control={control}
            render={({ field }) => (
              <StyledRadioGroup {...field} data-test="need-approval-radio-group">
                <div data-test="need-approval-yes"><Radio value={true}>{intl.formatMessage({ id: "yes" })}</Radio></div>
                <div data-test="need-approval-no"><Radio value={false}>{intl.formatMessage({ id: "no" })}</Radio></div>
              </StyledRadioGroup>
            )}
          />
        </StyledFormItem>

        {!needApproval && (
          <SpaceInfoDontNeedApproval>
            <div>
              <RxInfoCircled size={22} color={"#314158"} />
            </div>

            <div>
              <TextDontNeedApproval>
                {intl.formatMessage({ id: "info_dont_need_approval" })}
              </TextDontNeedApproval>
            </div>
          </SpaceInfoDontNeedApproval>
        )}

        <ScrollableContent>
          {needApproval &&
            fields.map((field, index) => (
              <div key={field.id} style={{ marginBottom: "16px" }}>
                <InstanceTitle>
                  {intl.formatMessage(
                    { id: "instance_with_number" },
                    { number: `${index + 1}` }
                  )}
                </InstanceTitle>

                <StyledFormItem
                  label={intl.formatMessage({ id: "approver_type" })}
                  required
                  validateStatus={
                    errors.instances?.[index]?.approverType ? "error" : ""
                  }
                  help={errors.instances?.[index]?.approverType?.message}
                >
                  <Controller
                    name={`instances.${index}.approverType`}
                    control={control}
                    render={({ field }) => (
                      <StyledRadioGroup {...field} data-test={`approver-type-radio-group-${index}`}>
                        <div data-test={`approver-type-profile-${index}`}>
                          <Radio
                            onClick={() =>
                              setValue(`instances[${index}].specifyUsers`, "")
                            }
                            value="profile"
                          >
                            {intl.formatMessage({ id: "profile" })}
                          </Radio>
                        </div>
                        <div data-test={`approver-type-users-${index}`}>
                          <Radio
                            onClick={() =>
                              setValue(`instances[${index}].approverProfile`, [])
                            }
                            value="users"
                          >
                            {intl.formatMessage({ id: "users" })}
                          </Radio>
                        </div>
                      </StyledRadioGroup>
                    )}
                  />
                </StyledFormItem>

                {watch(`instances[${index}].approverType`) === "profile" && (
                  <StyledFormItem
                    label={intl.formatMessage({ id: "approver_profile" })}
                    required
                    validateStatus={
                      errors.instances?.[index]?.approverProfile ? "error" : ""
                    }
                    help={errors.instances?.[index]?.approverProfile?.message}
                  >
                    <Controller
                      name={`instances[${index}].approverProfile`}
                      control={control}
                      render={({ field }) => (
                        <div data-testid={`approver-profile-select-${index}`}>
                          <Select
                            {...field}
                            popupClassName={`approver-profile-select-dropdown-${index}`}
                            mode="multiple"
                            showSearch
                            filterOption={(input, option) =>
                              option?.label
                                ?.toLowerCase()
                                .includes(input.toLowerCase())
                            }
                            options={approverProfiles}
                            placeholder={intl.formatMessage({
                              id: "approver_profile",
                            })}
                          />
                        </div>
                      )}
                    />
                  </StyledFormItem>
                )}

                {watch(`instances[${index}].approverType`) === "users" && (
                  <StyledFormItem
                    label={intl.formatMessage({ id: "specify_user" })}
                    required
                    validateStatus={
                      errors.instances?.[index]?.specifyUsers ? "error" : ""
                    }
                    help={errors.instances?.[index]?.specifyUsers?.message}
                  >
                    <Controller
                      name={`instances[${index}].specifyUsers`}
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          data-test={`specify-users-input-${index}`}
                          placeholder={intl.formatMessage({
                            id: "event.linkFlow.placeholder_input_specify_users",
                          })}
                        />
                      )}
                    />
                  </StyledFormItem>
                )}

                {index > 0 && (
                  <RemoveInstanceButton data-test={`remove-instance-${index}`} onClick={() => remove(index)}>
                    <FaRegTrashAlt />
                    {intl.formatMessage({ id: "remove_instance" })} {index + 1}
                  </RemoveInstanceButton>
                )}
              </div>
            ))}
        </ScrollableContent>

        <div style={{ justifyContent: "flex-end", display: "flex" }}>
          {needApproval && (
            <AddAnotherInstanceButton
              data-test="add-another-instance-button"
              onClick={() => append({ ...DEFAULT_INSTANCE })}
            >
              {intl.formatMessage({ id: "add_another_instance" })}
            </AddAnotherInstanceButton>
          )}
        </div>

        <div style={{ marginTop: "24px", textAlign: "right" }}>
          <StyledButton
            isCancel
            style={{ marginRight: "8px" }}
            onClick={closeModal}
          >
            <span data-test="event-upload-flow-cancel-button">{intl.formatMessage({ id: "cancel" })}</span>
          </StyledButton>

          <StyledButton type="primary" htmlType="submit">
            <span data-test="event-upload-flow-save-button">{intl.formatMessage({ id: "save" })}</span>
          </StyledButton>
        </div>
      </Form>

      <CustomModalConfirmation
        show={modalConfirmIsOpen}
        intl={intl}
        title={intl.formatMessage({ id: "custom.modal.confirmation.title" })}
        description={intl.formatMessage({
          id: "custom.modal.confirmation.description",
        })}
        questionConfirm={intl.formatMessage({
          id: "custom.modal.confirmation.question.confirm",
        })}
        handleClose={() => setModalConfirmIsOpen(false)}
        handleConfirm={() => {
          setModalConfirmIsOpen(false);
          submitToAPI(lastFormData);
        }}
        confirmText={"confirm"}
        cancelText={"goback"}
        IconConfirm={() => <IoCheckmarkDoneOutline size={20} />}
        IconCancel={() => <IoReturnUpBackOutline size={24} />}
      />
    </StyledModal>
  );
};

export default EventUploadFlowModal;
